/**
 * End-of-Term Summary Component (Phase 3 — Step 3.12).
 *
 * Auto-generated report with final stats and SVG charts.
 */

import React, { useMemo } from 'react';
import { useAppStore } from '@/store';
import { computeEffectiveAbsences, computeAttendancePercentage, computeMaxAbsences } from '@abs/shared-logic';
import { resolveActiveTier, computeTotalDeduction } from '@abs/shared-logic';
import { classifyRisk, getRiskLevelColor } from '@abs/shared-logic';
import { computeTotalSessions } from '@abs/shared-logic';
import type { AbsenceEntry, Course } from '@abs/shared-types';
import { StatCard } from '../shared';

interface TermSummaryProps {
  course: Course;
}

export function TermSummary({ course }: TermSummaryProps): React.ReactElement {
  const policy = useAppStore((s) => s.policies.find((p) => p.id === course.policyId));
  const entries = useAppStore((s) => s.entries.filter((e: AbsenceEntry) => e.courseId === course.id));

  const stats = useMemo(() => {
    if (!policy) return null;

    const { total } = computeTotalSessions(
      course.meetingPatterns, course.sessionOverrides,
      course.startDate, course.endDate,
    );

    const effective = computeEffectiveAbsences(entries, policy);
    const maxAbs = computeMaxAbsences(policy, total);
    const remaining = Math.max(0, maxAbs - effective.totalPenaltyAbsences);
    const pct = computeAttendancePercentage(total, effective.totalPenaltyAbsences);
    const { activeTier, nextTier } = resolveActiveTier(policy.tiers, effective.totalPenaltyAbsences);
    const risk = classifyRisk(remaining, maxAbs, pct, policy.attendanceFloor, activeTier, nextTier, effective.totalPenaltyAbsences);
    const deduction = course.baseGrade !== null ? computeTotalDeduction(policy, effective) : 0;
    const finalGrade = course.baseGrade !== null ? Math.max(0, course.baseGrade - deduction) : null;

    // Budget consumption gauge
    const consumed = maxAbs > 0 ? effective.totalPenaltyAbsences / maxAbs : 0;

    // Breakdown by type
    const byType = {
      unexcused: entries.filter((e) => e.type === 'unexcused').length,
      excused: entries.filter((e) => e.type === 'excused').length,
      late: entries.filter((e) => e.type === 'late').length,
      early: entries.filter((e) => e.type === 'early_departure').length,
    };

    return {
      total,
      effective,
      maxAbs,
      remaining,
      pct,
      risk,
      activeTier,
      finalGrade,
      deduction,
      consumed,
      byType,
    };
  }, [course, entries, policy]);

  if (!stats || !policy) {
    return (
      <div className="term-summary" role="region" aria-label="Term Summary">
        <h4>End-of-Term Summary</h4>
        <p>No policy configured for this course.</p>
      </div>
    );
  }

  // SVG gauge for budget consumption
  const GAUGE_R = 50;
  const GAUGE_CX = 60;
  const GAUGE_CY = 60;
  const CIRC = 2 * Math.PI * GAUGE_R;
  const consumedArc = Math.min(stats.consumed, 1) * CIRC;
  const gaugeColor = getRiskLevelColor(stats.risk);

  return (
    <div className="term-summary" role="region" aria-label="End-of-Term Summary">
      <h4>End-of-Term Summary for {course.name}</h4>

      <div className="term-summary__grid">
        <div className="term-summary__gauge">
          <svg viewBox="0 0 120 120" width={120} height={120} role="img" aria-label={`Budget consumed: ${Math.round(stats.consumed * 100)}%`}>
            <circle cx={GAUGE_CX} cy={GAUGE_CY} r={GAUGE_R} fill="none" stroke="var(--color-bg-secondary)" strokeWidth={10} />
            <circle
              cx={GAUGE_CX}
              cy={GAUGE_CY}
              r={GAUGE_R}
              fill="none"
              stroke={gaugeColor}
              strokeWidth={10}
              strokeDasharray={`${consumedArc} ${CIRC}`}
              strokeDashoffset={CIRC / 4}
              strokeLinecap="round"
            />
            <text x={GAUGE_CX} y={GAUGE_CY} textAnchor="middle" dominantBaseline="middle" fontSize={16} fontWeight="bold" fill="var(--text)">
              {Math.round(stats.pct)}%
            </text>
          </svg>
          <span className="term-summary__gauge-label">Attendance</span>
          <p className="stat-caption">Centre value is attendance %; arc fill is the share of your absence budget consumed.</p>
        </div>

        <div className="term-summary__stats">
          <StatCard value={stats.effective.totalPenaltyAbsences} label="Absences Used" />
          <StatCard value={stats.remaining} label="Remaining" />
          <StatCard value={stats.total} label="Total Sessions" />
          {stats.finalGrade !== null && (
            <StatCard value={`${stats.finalGrade.toFixed(1)}%`} label="Final Grade" />
          )}
        </div>
      </div>

      <div className="term-summary__breakdown">
        <h5>Absence Breakdown</h5>
        <div className="term-summary__breakdown-row">
          <span>Unexcused: {stats.byType.unexcused}</span>
          <span>Excused: {stats.byType.excused}</span>
          <span>Late: {stats.byType.late}</span>
          <span>Early Departure: {stats.byType.early}</span>
        </div>
      </div>

      {stats.activeTier && (
        <div className="term-summary__penalty">
          <h5>Active Penalty</h5>
          <p>{stats.activeTier.description}</p>
          {course.baseGrade !== null && (
            <p>Deduction: −{stats.deduction.toFixed(1)} points from base grade of {course.baseGrade}%</p>
          )}
        </div>
      )}
    </div>
  );
}
